import React from 'react';
import type { ColourCombination, ColourFinish } from '../../../types';
import type { CanvasSectionBlock } from '../sectionRegistry';
import {
  findModelViewerOptionByReference,
} from '../../../lib/modelViewerCatalog';
import { resolveViewerSurfacePresentation } from '../../../lib/viewerSurfaceStyles';
import {
  isAtmosphericGlowColor,
  mergeViewerPipelineOverrides,
  normalizeHexColor,
  type ViewerRandomSelection,
} from '../modelViewerStage';
import { resolveResponsiveLayoutBreakpoint } from '../layoutEngine/blockFrame';
import { useOptionalVisualCustomisationDraft } from '../../../hooks/useVisualCustomisationDraft';
import { useMaterialProperties } from '../../../hooks/useMaterialProperties';
import {
  readSceneLayerToggle,
  SECTION_FLOOR_SPILL_LAYER_TOGGLE_KEYS,
  applySectionWebglLayerToggles,
  resolveSectionFloorPipelineDefaults,
} from '../../../lib/modelViewerSceneLayerRegistry';
import { filterModelViewerCompositionPipelineOverrides } from '../../../lib/modelViewerPipelineOwnership';
import {
  materialContextForFinish,
  materialKeyForFinish,
} from '../../../lib/materialByFinish';
import { materialFallbackFromProperties } from '../../../lib/visualPreviewSettings';
import {
  resolveSectionSceneArt,
  resolveSectionSceneBacklightLayers,
  resolveSectionSceneBaseMistStyle,
  resolveSectionSceneFloorBaseFillStyle,
  resolveSectionSceneFloorSpillLayers,
  resolveSectionForegroundReflectionVeilStyle,
  resolveSectionSceneLayerMode,
  resolveSectionWallLightRevealStyle,
  resolveModelViewerSceneContentForViewport,
} from './modelViewerSceneBackdrop';
import { PUBLIC_VIEWER_ENGINE } from '../../../lib/productViewerEngine';
import type { PublicRenderPipelineOverrides } from '../../../lib/publicRenderPipelines';

const ModelViewer = React.lazy(() => import('../../ModelViewer'));
const VaseStage3D = React.lazy(() =>
  import('../../vaseStage3d').then((module) => ({ default: module.VaseStage3D }))
);

function finiteNumber(value: unknown, fallback: number) {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

type WrapperProps = React.HTMLAttributes<HTMLDivElement> & {
  style?: React.CSSProperties;
};

type ModelViewerBlockRendererProps = {
  block: CanvasSectionBlock;
  blockWrapperProps: WrapperProps;
  blockStyle: React.CSSProperties;
  selectableBlockHitProps: Record<string, string>;
  selectionTarget: string;
  renderSelectionOverlay: (block: CanvasSectionBlock) => React.ReactNode;
  isAdmin: boolean;
  isInteractPreview: boolean;
  isImmersivePreview: boolean;
  isLayoutEditing?: boolean;
  showLayoutFrames?: boolean;
  adminViewport?: 'desktop' | 'tablet' | 'mobile';
  blockSelected: boolean;
  visibleColourCombinations: ColourCombination[];
  randomSelection?: ViewerRandomSelection;
};

export function ModelViewerBlockRenderer({
  block,
  blockWrapperProps,
  blockStyle,
  selectableBlockHitProps,
  selectionTarget,
  renderSelectionOverlay,
  isAdmin,
  isInteractPreview,
  isImmersivePreview,
  isLayoutEditing = false,
  showLayoutFrames = false,
  adminViewport,
  blockSelected,
  visibleColourCombinations,
  randomSelection,
}: ModelViewerBlockRendererProps) {
  const visualDraftStore = useOptionalVisualCustomisationDraft();
  const hasRenderableVisualDraft = Boolean(
    isAdmin &&
      visualDraftStore &&
      (visualDraftStore.changeFlags.pipeline ||
        visualDraftStore.changeFlags.lighting ||
        visualDraftStore.changeFlags.materials ||
        visualDraftStore.changeFlags.environmentRegistry)
  );
  const visualDraft = hasRenderableVisualDraft ? visualDraftStore?.draft : undefined;
  const content =
    block.content && typeof block.content === 'object'
      ? (block.content as Record<string, unknown>)
      : {};
  const modelPath = String(content.modelPath ?? '').trim();
  const posterSrc = String(content.posterSrc ?? '').trim();
  const posterAlt = String(content.posterAlt ?? '3D model preview');
  const vaseId = Number.isFinite(content.vaseId) ? Number(content.vaseId) : undefined;
  const modelSelectionMode = content.modelSelectionMode === 'random' ? 'random' : 'fixed';
  const colourCombinationSelectionMode =
    content.colourCombinationSelectionMode === 'random' ? 'random' : 'fixed';
  const resolvedModelOption =
    modelSelectionMode === 'random'
      ? findModelViewerOptionByReference({
          vaseId: randomSelection?.vaseId,
          modelPath: randomSelection?.modelPath,
        })
      : findModelViewerOptionByReference({
          vaseId,
          modelPath,
        });
  const resolvedColourCombination =
    colourCombinationSelectionMode === 'random'
      ? visibleColourCombinations.find(
          (entry) => entry.id === randomSelection?.colourCombinationId
        ) ?? null
      : typeof content.colourCombinationId === 'string' && content.colourCombinationId.trim().length > 0
        ? visibleColourCombinations.find(
            (entry) => entry.id === content.colourCombinationId
          ) ?? null
        : null;
  const resolvedModelPath = resolvedModelOption?.value ?? modelPath;
  const resolvedPosterSrc = resolvedModelOption?.posterSrc ?? posterSrc;
  const resolvedPosterAlt =
    resolvedModelOption?.label ? `${resolvedModelOption.label} preview` : posterAlt;
  const resolvedVaseId = resolvedModelOption?.vaseId ?? vaseId;
  const finishType =
    typeof resolvedColourCombination?.finish_type === 'string' &&
    resolvedColourCombination.finish_type.trim().length > 0
      ? (resolvedColourCombination.finish_type.trim() as ColourFinish)
      : typeof content.finishType === 'string' && content.finishType.trim().length > 0
        ? (content.finishType.trim() as ColourFinish)
        : undefined;
  const materialContext = materialContextForFinish(finishType);
  const { properties: savedMaterialProperties } = useMaterialProperties(
    materialContext,
    {
      enabled: Boolean(visualDraft),
      fallbackContext: materialContext === 'global' ? undefined : 'global',
    }
  );
  const materialFallback = React.useMemo(
    () => materialFallbackFromProperties(savedMaterialProperties),
    [savedMaterialProperties]
  );
  const selectedColors =
    Array.isArray(resolvedColourCombination?.colours) &&
    resolvedColourCombination.colours.length > 0
      ? resolvedColourCombination.colours.filter(
          (entry): entry is string => typeof entry === 'string' && entry.trim().length > 0
        )
      : Array.isArray(content.selectedColors)
        ? content.selectedColors.filter(
            (entry): entry is string => typeof entry === 'string' && entry.trim().length > 0
          )
        : [];
  const surface = String(content.surface ?? '').toLowerCase();
  const immersiveSurface = surface === 'immersive' || surface === 'full-bleed';
  const renderPresetId = String(content.renderPresetId ?? content.publicLightingMode ?? '').toLowerCase();
  const publicLightingMode = renderPresetId === 'hero' ? 'hero' : 'product';
  const viewerSurfacePresentation = resolveViewerSurfacePresentation(
    content,
    immersiveSurface ? 'model-immersive' : 'model-inline'
  );
  const basePipelineOverrides =
    content.publicPipelineOverrides && typeof content.publicPipelineOverrides === 'object'
      ? (content.publicPipelineOverrides as Record<string, unknown>)
      : undefined;
  const activeViewerViewport =
    isAdmin && adminViewport
      ? adminViewport
      : typeof window === 'undefined'
        ? 'desktop'
        : resolveResponsiveLayoutBreakpoint(window.innerWidth);
  const sceneContent = resolveModelViewerSceneContentForViewport(
    content as Record<string, unknown>,
    activeViewerViewport
  );
  const responsivePipelineOverridesRoot =
    content.responsivePipelineOverrides &&
    typeof content.responsivePipelineOverrides === 'object' &&
    !Array.isArray(content.responsivePipelineOverrides)
      ? (content.responsivePipelineOverrides as Record<string, unknown>)
      : {};
  const responsivePipelineOverrides =
    activeViewerViewport !== 'desktop' &&
    responsivePipelineOverridesRoot[activeViewerViewport] &&
    typeof responsivePipelineOverridesRoot[activeViewerViewport] === 'object' &&
    !Array.isArray(responsivePipelineOverridesRoot[activeViewerViewport])
      ? (responsivePipelineOverridesRoot[activeViewerViewport] as Record<string, unknown>)
      : undefined;
  const linkedDraftPipelineOverrides =
    visualDraft
      ? ({
          ...visualDraft.pipelines[publicLightingMode],
          lighting: visualDraft.lighting[publicLightingMode],
        } as Record<string, unknown>)
      : undefined;
  const stageAtmosphere = String(content.stageAtmosphere ?? '').toLowerCase();
  const stageGrounding = String(content.stageGrounding ?? '').toLowerCase();
  const sceneLayerMode = resolveSectionSceneLayerMode(content, {
    immersiveSurface,
    publicLightingMode,
    stageAtmosphere,
  });
  const useSectionSceneLayer = sceneLayerMode === 'section-backdrop';
  const useVaseStage3dContained =
    !useSectionSceneLayer &&
    PUBLIC_VIEWER_ENGINE === 'vaseStage3d' &&
    Boolean(resolvedModelPath);
  const sectionFloorPipelineDefaults =
    useSectionSceneLayer
      ? applySectionWebglLayerToggles(
          sceneContent as Record<string, unknown>,
          resolveSectionFloorPipelineDefaults(sceneContent as Record<string, unknown>)
        )
      : undefined;
  const pipelineOverrides = mergeViewerPipelineOverrides(
    linkedDraftPipelineOverrides,
    sectionFloorPipelineDefaults,
    filterModelViewerCompositionPipelineOverrides(basePipelineOverrides),
    filterModelViewerCompositionPipelineOverrides(responsivePipelineOverrides)
  );
  const materialKey = materialKeyForFinish(finishType);
  const draftMaterialForFinish =
    visualDraft?.materials?.[materialKey] ??
    (materialKey === 'default' ? visualDraft?.materials?.default : undefined);
  const linkedDraftMaterialSettings = visualDraft
    ? {
        ...materialFallback,
        ...(draftMaterialForFinish ?? {}),
      }
    : undefined;
  const showBoundedStageAtmosphere =
    stageAtmosphere === 'luxury' && !useSectionSceneLayer && !useVaseStage3dContained;
  const showBoundedStageGrounding =
    stageGrounding === 'soft-gallery' && !useSectionSceneLayer && !useVaseStage3dContained;
  const stageFit = String(content.stageFit ?? '').toLowerCase();
  const useContainStage = stageFit === 'contain';
  const stageMaxWidth = Math.max(20, Math.min(100, finiteNumber(content.stageMaxWidthPercent, 100)));
  const stageMaxHeight = Math.max(20, Math.min(100, finiteNumber(content.stageMaxHeightPercent, 100)));
  const stageVerticalAlign = String(content.stageVerticalAlign ?? 'center').toLowerCase();
  const stageHorizontalAlign = String(content.stageHorizontalAlign ?? 'center').toLowerCase();
  const viewerFrameStyle: React.CSSProperties = useContainStage
    ? {
        width: '100%',
        height: '100%',
        maxWidth: `${stageMaxWidth}%`,
        maxHeight: `${stageMaxHeight}%`,
      }
    : {};
  const viewerFrameClassName = useContainStage
    ? `relative z-[1] flex h-full w-full ${
        stageHorizontalAlign === 'left'
          ? 'justify-start'
          : stageHorizontalAlign === 'right'
            ? 'justify-end'
            : 'justify-center'
      } ${
        stageVerticalAlign === 'bottom'
          ? 'items-end'
          : stageVerticalAlign === 'top'
            ? 'items-start'
            : 'items-center'
      } ${isAdmin && !isInteractPreview ? 'pointer-events-none' : ''}`
    : `relative z-[1] h-full w-full ${isAdmin && !isInteractPreview ? 'pointer-events-none' : ''}`;
  const atmosphericGlowColors = selectedColors
    .map((entry) => normalizeHexColor(entry))
    .filter((entry): entry is string => !!entry && isAtmosphericGlowColor(entry));
  const primaryGlowColor = atmosphericGlowColors[0] ?? '#c8a45d';
  const secondaryGlowColor =
    atmosphericGlowColors.find((entry) => entry !== primaryGlowColor) ?? primaryGlowColor;
  const stageAtmosphereStyle: React.CSSProperties =
    showBoundedStageAtmosphere
      ? {
          backgroundImage: [
            `radial-gradient(ellipse at 38% 78%, ${primaryGlowColor}18 0%, ${primaryGlowColor}08 18%, transparent 46%)`,
            `radial-gradient(ellipse at 78% 66%, ${secondaryGlowColor}1a 0%, ${secondaryGlowColor}08 20%, transparent 48%)`,
            `linear-gradient(108deg, transparent 0%, ${primaryGlowColor}05 34%, transparent 60%)`,
            `linear-gradient(248deg, transparent 12%, ${secondaryGlowColor}06 46%, transparent 70%)`,
            'linear-gradient(180deg, rgba(0,0,0,0.56) 0%, rgba(0,0,0,0.18) 54%, rgba(0,0,0,0.96) 100%)',
          ].join(', '),
          filter: 'blur(0.25px)',
          maskImage: 'radial-gradient(ellipse at 58% 70%, black 0%, black 34%, transparent 74%)',
          WebkitMaskImage: 'radial-gradient(ellipse at 58% 70%, black 0%, black 34%, transparent 74%)',
          opacity: 0.42,
        }
      : {};
  const stageHorizonStyle: React.CSSProperties =
    showBoundedStageAtmosphere
      ? {
          backgroundImage: [
            `radial-gradient(ellipse at 36% 62%, ${primaryGlowColor}32 0%, ${primaryGlowColor}14 18%, transparent 58%)`,
            `radial-gradient(ellipse at 74% 58%, ${secondaryGlowColor}2c 0%, ${secondaryGlowColor}12 20%, transparent 58%)`,
            `linear-gradient(90deg, transparent 0%, ${primaryGlowColor}20 34%, ${primaryGlowColor}10 48%, ${secondaryGlowColor}1c 66%, transparent 100%)`,
          ].join(', '),
          filter: 'blur(12px)',
          transform: 'translateY(16%) scaleX(1.22)',
          maskImage: 'linear-gradient(180deg, transparent 0%, black 34%, black 60%, transparent 100%)',
          WebkitMaskImage: 'linear-gradient(180deg, transparent 0%, black 34%, black 60%, transparent 100%)',
          opacity: 0.58,
        }
      : {};
  const stageGroundingStyle: React.CSSProperties =
    showBoundedStageGrounding
      ? {
          backgroundImage: [
            `radial-gradient(ellipse at 44% 54%, ${primaryGlowColor}36 0%, ${primaryGlowColor}16 18%, transparent 50%)`,
            `radial-gradient(ellipse at 64% 53%, ${secondaryGlowColor}2c 0%, ${secondaryGlowColor}10 20%, transparent 50%)`,
            'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.032) 45%, rgba(255,255,255,0.016) 58%, transparent 100%)',
          ].join(', '),
          filter: 'blur(9px)',
          transform: 'translateY(18%) scaleX(1.34)',
          maskImage: 'radial-gradient(ellipse at 50% 56%, black 0%, black 42%, transparent 76%)',
          WebkitMaskImage: 'radial-gradient(ellipse at 50% 56%, black 0%, black 42%, transparent 76%)',
          opacity: 0.66,
        }
      : {};
  const stageContactStyle: React.CSSProperties =
    showBoundedStageGrounding
      ? {
          backgroundImage: [
            'radial-gradient(ellipse at 50% 54%, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.46) 24%, rgba(0,0,0,0.18) 48%, transparent 72%)',
            `radial-gradient(ellipse at 46% 52%, ${primaryGlowColor}14 0%, transparent 48%)`,
            `radial-gradient(ellipse at 62% 52%, ${secondaryGlowColor}10 0%, transparent 46%)`,
          ].join(', '),
          filter: 'blur(8px)',
          transform: 'translateY(22%) scaleX(0.92)',
          maskImage: 'radial-gradient(ellipse at 50% 50%, black 0%, black 54%, transparent 78%)',
          WebkitMaskImage: 'radial-gradient(ellipse at 50% 50%, black 0%, black 54%, transparent 78%)',
          opacity: 0.78,
        }
      : {};
  const sectionBacklightLayers =
    useSectionSceneLayer
      ? resolveSectionSceneBacklightLayers(sceneContent, primaryGlowColor, secondaryGlowColor, true)
      : null;
  const sectionSceneArt =
    useSectionSceneLayer
      ? resolveSectionSceneArt(sceneContent, true, primaryGlowColor, secondaryGlowColor)
      : null;
  const sectionForegroundReflectionVeilStyle =
    useSectionSceneLayer
      ? resolveSectionForegroundReflectionVeilStyle(sceneContent, true)
      : null;
  const sectionWallLightStyle =
    useSectionSceneLayer
      ? resolveSectionWallLightRevealStyle(sceneContent, primaryGlowColor, secondaryGlowColor, true)
      : null;
  const sectionBacklightWallIlluminationStyle =
    useSectionSceneLayer && sectionBacklightLayers?.wallIlluminationStyle
      ? sectionBacklightLayers.wallIlluminationStyle
      : null;
  const sectionBaseMistStyle =
    useSectionSceneLayer
      ? resolveSectionSceneBaseMistStyle(sceneContent, primaryGlowColor, secondaryGlowColor, true)
      : null;
  const sectionFloorBaseFillStyle =
    useSectionSceneLayer &&
    readSceneLayerToggle(sceneContent as Record<string, unknown>, 'sectionSceneFloorBaseFillEnabled')
      ? resolveSectionSceneFloorBaseFillStyle(sceneContent)
      : null;
  const pauseSectionBackdropLiveViewer = Boolean(
    useSectionSceneLayer &&
      isAdmin &&
      isImmersivePreview &&
      isLayoutEditing &&
      !blockSelected &&
      !isInteractPreview
  );
  const isNarrowAdminPreview =
    isAdmin && (adminViewport === 'mobile' || adminViewport === 'tablet');
  const shouldMountLiveViewer = Boolean(
    resolvedModelPath &&
      (!isAdmin ||
        isInteractPreview ||
        blockSelected ||
        isNarrowAdminPreview ||
        (isImmersivePreview && !pauseSectionBackdropLiveViewer))
  );
  const { onMouseEnter, onMouseLeave, ...blockWrapperPropsWithoutHover } = blockWrapperProps;
  const sceneHotspotHoverHandlers =
    onMouseEnter && onMouseLeave ? { onMouseEnter, onMouseLeave } : {};
  const shellClassName = immersiveSurface
    ? `relative h-full w-full ${useSectionSceneLayer ? 'overflow-visible' : 'overflow-hidden'}`
    : 'relative h-full w-full overflow-hidden rounded-[26px] border border-stone-200 shadow-sm';
  const shellStyle = useSectionSceneLayer ? undefined : viewerSurfacePresentation.shellStyle;
  const viewerFrameClassNameResolved = useSectionSceneLayer
    ? 'relative z-[2] h-full w-full pointer-events-none'
    : viewerFrameClassName;
  const viewerFrameStyleResolved: React.CSSProperties = useSectionSceneLayer
    ? { width: '100%', height: '100%' }
    : viewerFrameStyle;
  // Additive glow only — opaque spill layers paint over WebGL reflections.
  const ADDITIVE_SECTION_FLOOR_SPILL_IDS = new Set([
    'environment-floor-reflection',
    'horizon-floor-band',
    'horizon-floor-sheen',
    'colourway-floor-tail',
    'wall-light-floor-spill',
    'wall-floor-reflection',
    'symbol-floor-reflection',
  ]);
  const sectionFloorSpillLayers = useSectionSceneLayer
    ? resolveSectionSceneFloorSpillLayers(sceneContent, {
        primaryGlowColor,
        secondaryGlowColor,
        sceneArt: sectionSceneArt,
        backlightLayers: sectionBacklightLayers,
        wallLightStyle: sectionWallLightStyle,
      })
      .filter((layer) => ADDITIVE_SECTION_FLOOR_SPILL_IDS.has(layer.id))
      .filter((layer) => {
        const toggleKey = SECTION_FLOOR_SPILL_LAYER_TOGGLE_KEYS[layer.id];
        if (!toggleKey) return true;
        return readSceneLayerToggle(sceneContent as Record<string, unknown>, toggleKey);
      })
    : [];
  const viewerLoadingFallback = (
    <div
      className={`flex h-full w-full items-center justify-center text-sm font-medium ${
        viewerSurfacePresentation.tone === 'dark' ? 'text-white' : 'text-stone-700'
      }`}
    >
      Loading 3D preview...
    </div>
  );
  const modelViewerCommonProps = {
    modelPath: resolvedModelPath,
    vaseId: resolvedVaseId,
    selectedColors,
    finishType,
    isPreview: true as const,
    publicLightingMode,
    publicPipelineOverrides: pipelineOverrides as any,
    materialSettings: linkedDraftMaterialSettings,
    environmentRegistry: visualDraft?.environmentRegistryDraft,
    environmentRegistryMode: visualDraft ? ('draft' as const) : undefined,
    backgroundColor: viewerSurfacePresentation.canvasColor,
    interactionMode: 'turntable' as const,
    enableZoom: isInteractPreview,
    transparentBackground: viewerSurfacePresentation.transparentCanvas,
    sectionBackdrop: useSectionSceneLayer,
    sectionSceneHorizonY:
      typeof sceneContent.sectionSceneHorizonY === 'number' &&
      Number.isFinite(sceneContent.sectionSceneHorizonY)
        ? sceneContent.sectionSceneHorizonY
        : typeof sceneContent.sectionBacklightY === 'number' &&
            Number.isFinite(sceneContent.sectionBacklightY)
          ? sceneContent.sectionBacklightY
          : undefined,
    containerClassName: 'h-full w-full',
  };

  const sceneBackdropShell = (
    <div className={shellClassName} style={shellStyle}>
      {sectionSceneArt ? (
        <>
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-0"
            style={sectionSceneArt.wallWrapperStyle}
          >
            {sectionSceneArt.textureStyle ? (
              <div className="absolute inset-0" style={sectionSceneArt.textureStyle} />
            ) : null}
            {sectionSceneArt.textureDepthShadowStyle ? (
              <div className="absolute inset-0" style={sectionSceneArt.textureDepthShadowStyle} />
            ) : null}
            {sectionSceneArt.textureDepthHighlightStyle ? (
              <div className="absolute inset-0" style={sectionSceneArt.textureDepthHighlightStyle} />
            ) : null}
            {sectionSceneArt.textureLightRevealStyle ? (
              <div className="absolute inset-0" style={sectionSceneArt.textureLightRevealStyle} />
            ) : null}
          </div>
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-0"
            style={sectionSceneArt.symbolWrapperStyle}
          >
            {sectionSceneArt.symbolBakedOverlayStyle ? (
              <div className="absolute inset-0" style={sectionSceneArt.symbolBakedOverlayStyle} />
            ) : null}
            {sectionSceneArt.symbolAuraStyle ? (
              <div className="absolute inset-0" style={sectionSceneArt.symbolAuraStyle} />
            ) : null}
            {sectionSceneArt.symbolEffectLayerStyles.map((style, index) => (
              <div key={`symbol-effect-${index}`} className="absolute inset-0" style={style} />
            ))}
            {sectionSceneArt.symbolStyle ? (
              <div className="absolute inset-0" style={sectionSceneArt.symbolStyle} />
            ) : null}
          </div>
        </>
      ) : null}
      {sectionFloorBaseFillStyle ? (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute z-[1]"
          style={sectionFloorBaseFillStyle}
        />
      ) : null}
      {sectionWallLightStyle ? (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute z-[1]"
          style={sectionWallLightStyle}
        />
      ) : null}
      {sectionBacklightWallIlluminationStyle && !sectionWallLightStyle ? (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute z-[1]"
          style={sectionBacklightWallIlluminationStyle}
        />
      ) : null}
      {sectionBacklightLayers ? (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute z-[1]"
          style={sectionBacklightLayers.bloomStyle}
        />
      ) : null}
      {/* Floor tint + art reflections sit below WebGL so the reflector and model stay in front. */}
      {useSectionSceneLayer && sectionFloorSpillLayers.length > 0 ? (
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-[2] mix-blend-screen">
          {sectionFloorSpillLayers.map((layer) => (
            <div key={layer.id} style={layer.style} />
          ))}
        </div>
      ) : null}
      {sectionBacklightLayers ? (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-[2]"
          style={sectionBacklightLayers.coreStyle}
        />
      ) : null}
      {sectionBaseMistStyle ? (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-[2] mix-blend-screen"
          style={sectionBaseMistStyle}
        />
      ) : null}
      {useSectionSceneLayer && shouldMountLiveViewer ? (
        <div className="pointer-events-none absolute inset-0 z-[3]">
          <React.Suspense fallback={viewerLoadingFallback}>
            <ModelViewer
              {...modelViewerCommonProps}
              adminPerformanceMode={false}
            />
          </React.Suspense>
        </div>
      ) : shouldMountLiveViewer ? (
        <React.Suspense fallback={viewerLoadingFallback}>
          <div className={viewerFrameClassNameResolved}>
            <div className="h-full w-full" style={viewerFrameStyleResolved}>
              <ModelViewer
                {...modelViewerCommonProps}
                adminPerformanceMode={isAdmin}
              />
            </div>
          </div>
        </React.Suspense>
      ) : (
        <div
          className={`flex h-full w-full items-center justify-center text-sm ${
            viewerSurfacePresentation.tone === 'dark' ? 'text-white/70' : 'text-stone-500'
          }`}
        >
          3D showcase
        </div>
      )}
      {sectionForegroundReflectionVeilStyle ? (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute z-[4]"
          style={sectionForegroundReflectionVeilStyle}
        />
      ) : null}
    </div>
  );

  if (useSectionSceneLayer) {
    const showSceneSelectionAffordance = Boolean(
      isAdmin &&
        !isInteractPreview &&
        !blockSelected &&
        Object.keys(selectableBlockHitProps).length > 0
    );
    const interactionAnchorStyle: React.CSSProperties = {
      left: blockStyle.left,
      top: blockStyle.top,
      width: 0,
      height: 0,
      minWidth: 0,
      minHeight: 0,
      overflow: 'visible',
      pointerEvents: 'none',
      zIndex: typeof blockStyle.zIndex === 'number' ? blockStyle.zIndex : undefined,
    };

    return (
      <>
        <div
          className="pointer-events-none absolute left-0 top-0 h-full w-full overflow-hidden"
          style={{
            isolation: 'isolate',
            contain: 'layout paint style',
          }}
        >
          {sceneBackdropShell}
        </div>
        <div
          {...blockWrapperPropsWithoutHover}
          className={blockWrapperProps.className ?? 'pointer-events-auto absolute'}
          style={interactionAnchorStyle}
        >
          {showSceneSelectionAffordance ? (
            <button
              type="button"
              {...selectableBlockHitProps}
              {...sceneHotspotHoverHandlers}
              className="absolute z-[120] rounded-full border border-stone-500/80 bg-stone-950/90 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.06em] text-stone-100 shadow-sm transition hover:border-blue-300 hover:text-blue-100"
              style={{
                left: 8,
                bottom: 8,
                top: 'auto',
                width: 'auto',
                height: 'auto',
                minWidth: 'auto',
                minHeight: 'auto',
                transform: 'none',
                pointerEvents: 'auto',
              }}
              title="Select 3D scene — set vase position in Inspector (Mobile breakpoint)"
              aria-label="Select 3D scene"
            >
              3D scene
            </button>
          ) : null}
        </div>
      </>
    );
  }

  const rootWrapperProps = blockWrapperProps;
  const rootStyle = blockStyle;

  return (
    <div key={block.id} {...rootWrapperProps} style={rootStyle}>
      <div
        {...(selectionTarget !== 'content' ? selectableBlockHitProps : {})}
        className={shellClassName}
        style={shellStyle}
      >
        {showBoundedStageAtmosphere ? (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-0 mix-blend-screen transition-[background-image] duration-700"
            style={stageAtmosphereStyle}
          />
        ) : null}
        {showBoundedStageAtmosphere ? (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-[2%] bottom-[9%] z-0 h-[24%] mix-blend-screen"
            style={stageHorizonStyle}
          />
        ) : null}
        {sectionWallLightStyle ? (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute z-[1]"
            style={sectionWallLightStyle}
          />
        ) : null}
        {sectionBacklightLayers ? (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute z-[1]"
            style={sectionBacklightLayers.bloomStyle}
          />
        ) : null}
        {sectionBacklightLayers ? (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute z-[1]"
            style={sectionBacklightLayers.floorSheenStyle}
          />
        ) : null}
        {sectionBacklightLayers ? (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute z-[1]"
            style={sectionBacklightLayers.coreStyle}
          />
        ) : null}
        {sectionBaseMistStyle ? (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-[1] mix-blend-screen"
            style={sectionBaseMistStyle}
          />
        ) : null}
        {showBoundedStageGrounding ? (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-[6%] bottom-[4%] z-0 h-[24%] mix-blend-screen"
            style={stageGroundingStyle}
          />
        ) : null}
        {showBoundedStageGrounding ? (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-[26%] bottom-[5%] z-0 h-[16%]"
            style={stageContactStyle}
          />
        ) : null}
        {shouldMountLiveViewer ? (
          <React.Suspense
            fallback={
              <div
                className={`flex h-full w-full items-center justify-center text-sm font-medium ${
                  viewerSurfacePresentation.tone === 'dark' ? 'text-white' : 'text-stone-700'
                }`}
              >
                Loading 3D preview...
              </div>
            }
          >
            <div className={viewerFrameClassNameResolved}>
              <div className="h-full w-full" style={viewerFrameStyleResolved}>
                {useVaseStage3dContained ? (
                  <VaseStage3D
                    key={resolvedModelPath}
                    profile={publicLightingMode}
                    model={{
                      modelPath: resolvedModelPath,
                      vaseId: resolvedVaseId,
                      selectedColors,
                      finishType: finishType ?? 'shifting',
                    }}
                    interaction={{
                      enableDrag: isInteractPreview,
                      enableZoom: false,
                    }}
                    pipelineOverrides={pipelineOverrides as PublicRenderPipelineOverrides}
                    container={{
                      className: 'h-full w-full',
                      transparentBackground: viewerSurfacePresentation.transparentCanvas,
                      backgroundColor: viewerSurfacePresentation.canvasColor,
                    }}
                    adminPerformanceMode={isAdmin && !isInteractPreview}
                  />
                ) : (
                  <ModelViewer
                    modelPath={resolvedModelPath}
                    vaseId={resolvedVaseId}
                    selectedColors={selectedColors}
                    finishType={finishType}
                    isPreview
                    publicLightingMode={publicLightingMode}
                    publicPipelineOverrides={pipelineOverrides as any}
                    materialSettings={linkedDraftMaterialSettings}
                    environmentRegistry={visualDraft?.environmentRegistryDraft}
                    environmentRegistryMode={visualDraft ? 'draft' : undefined}
                    backgroundColor={viewerSurfacePresentation.canvasColor}
                    interactionMode="turntable"
                    enableZoom={isInteractPreview}
                    transparentBackground={viewerSurfacePresentation.transparentCanvas}
                    containerClassName="h-full w-full"
                    adminPerformanceMode={isAdmin}
                  />
                )}
              </div>
            </div>
          </React.Suspense>
        ) : resolvedPosterSrc && !useSectionSceneLayer ? (
          <img
            src={resolvedPosterSrc}
            alt={resolvedPosterAlt}
            className="h-full w-full object-cover"
            loading="lazy"
            draggable={false}
          />
        ) : (
          <div
            className={`flex h-full w-full items-center justify-center text-sm ${
              viewerSurfacePresentation.tone === 'dark' ? 'text-white/70' : 'text-stone-500'
            }`}
          >
            3D showcase
          </div>
        )}
        {sectionForegroundReflectionVeilStyle ? (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute z-[3]"
            style={sectionForegroundReflectionVeilStyle}
          />
        ) : null}

        {isAdmin && !immersiveSurface && !shouldMountLiveViewer ? (
          <div className="pointer-events-none absolute inset-0 flex items-end justify-between bg-[linear-gradient(180deg,rgba(255,255,255,0.06),rgba(15,23,42,0.18))] p-3 text-[11px] font-medium text-stone-700">
            <span className="rounded-full border border-white/70 bg-white/85 px-2.5 py-1 shadow-sm">
              3D Showcase
            </span>
            <span className="rounded-full border border-stone-200 bg-white/85 px-2.5 py-1 shadow-sm">
              {blockSelected ? 'Switch to Interact to turn' : 'Select to preview'}
            </span>
          </div>
        ) : null}
        {renderSelectionOverlay(block)}
      </div>
    </div>
  );
}
